import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "BedrockBulletin";
const SITE_TAGLINE = "Canada's Mining Intelligence Brief";
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1571223641822-b82408a0e705?w=1600&q=80";
const TWITTER_HANDLE = "@bedrockbulletin";

/**
 * Shared SEO component. Emits <title>, meta description, Open Graph,
 * Twitter Card, and (optionally) a JSON-LD script into <head>.
 *
 * Usage:
 *   <SEO title="Rook I Uranium" description="..." image="..." type="article" jsonLd={...} />
 */
export default function SEO({
  title,
  description,
  image,
  type = "website",
  jsonLd,
}) {
  const canonicalTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} · ${SITE_TAGLINE}`;
  const canonicalDescription =
    description ||
    "Independent intelligence on the projects, policies, commodities and companies shaping Canada's mining sector.";
  const canonicalImage = image || DEFAULT_IMAGE;
  const canonicalUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";

  // Inject / remove JSON-LD via direct DOM API because react-helmet-async
  // v3.0.0 does not portal <script> children into <head>.
  useEffect(() => {
    if (!jsonLd) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-bb-seo", "1");
    el.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [jsonLd]);

  return (
    <Helmet>
      <title>{canonicalTitle}</title>
      <meta name="description" content={canonicalDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={canonicalTitle} />
      <meta property="og:description" content={canonicalDescription} />
      <meta property="og:image" content={canonicalImage} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={canonicalTitle} />
      <meta name="twitter:description" content={canonicalDescription} />
      <meta name="twitter:image" content={canonicalImage} />
    </Helmet>
  );
}

// ---------- Schema builders ----------

const publisher = () => ({
  "@type": "Organization",
  name: SITE_NAME,
  url: typeof window !== "undefined" ? window.location.origin : "",
});

const absoluteUrl = (path) =>
  typeof window !== "undefined" ? `${window.location.origin}${path}` : path;

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  alternateName: "BedrockBulletin",
  description: `${SITE_NAME} — ${SITE_TAGLINE}`,
  url: typeof window !== "undefined" ? window.location.origin : "",
  publisher: publisher(),
});

export const collectionSchema = (name, description, path) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url: absoluteUrl(path),
  isPartOf: {
    "@type": "WebSite",
    name: SITE_NAME,
    url: typeof window !== "undefined" ? window.location.origin : "",
  },
});

export const newsArticleSchema = (article, extras = {}) => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline: article.title,
  description: article.excerpt,
  image: article.featuredImage || DEFAULT_IMAGE,
  datePublished: article.publishedDate,
  dateModified: article.publishedDate,
  articleSection: article.category,
  articleBody: article.content,
  inLanguage: "en-CA",
  author: {
    "@type": "Person",
    name: article.author,
  },
  publisher: publisher(),
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": absoluteUrl(`/news/${article.slug}`),
  },
  ...(extras.about && { about: extras.about }),
});

export const projectSchema = (project, company, region) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: project.name,
  description: project.summary,
  articleSection: "Project Watch",
  author: publisher(),
  publisher: publisher(),
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": absoluteUrl(`/projects/${project.slug}`),
  },
  about: [
    ...(company
      ? [
          {
            "@type": "Organization",
            name: company.name,
            url: absoluteUrl(`/companies/${company.slug}`),
          },
        ]
      : []),
    ...(region
      ? [
          {
            "@type": "Place",
            name: region.name,
            url: absoluteUrl(`/regions/${region.slug}`),
          },
        ]
      : []),
    ...(project.location && project.location.lat && project.location.lng
      ? [
          {
            "@type": "Place",
            name: project.location.nearestTown || project.name,
            geo: {
              "@type": "GeoCoordinates",
              latitude: project.location.lat,
              longitude: project.location.lng,
            },
          },
        ]
      : []),
  ],
  keywords: (project.commodity || []).join(", "),
});

export const companySchema = (company, projects = []) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: company.name,
  description: company.description,
  url: company.website || absoluteUrl(`/companies/${company.slug}`),
  ...(company.headquarters && {
    address: { "@type": "PostalAddress", addressLocality: company.headquarters },
  }),
  ...(company.ticker &&
    company.ticker !== "—" && {
      tickerSymbol: company.ticker,
    }),
  identifier: company.ticker,
  knowsAbout: company.commodities || [],
  subOrganization: projects.map((p) => ({
    "@type": "Article",
    name: p.name,
    url: absoluteUrl(`/projects/${p.slug}`),
  })),
});

export const regionSchema = (region) => ({
  "@context": "https://schema.org",
  "@type": "Place",
  name: `${region.name}, Canada`,
  description: region.summary,
  address: { "@type": "PostalAddress", addressCountry: "CA", addressRegion: region.name },
  additionalType: "https://schema.org/AdministrativeArea",
  url: absoluteUrl(`/regions/${region.slug}`),
});

export const commoditySchema = (commodity) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: `${commodity.name} — Canadian mining coverage`,
  description: commodity.canadianContext || commodity.description,
  articleSection: "Commodities",
  author: publisher(),
  publisher: publisher(),
  keywords: [
    commodity.name,
    commodity.category,
    ...(commodity.criticalMineral ? ["critical mineral"] : []),
    ...(commodity.uses || []),
  ].join(", "),
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": absoluteUrl(`/commodities/${commodity.slug}`),
  },
});
